/**
 * MenuAssignmentsPanel — zakładka „Menu" w Centrum uprawnień (F24, menu per user/rola/zespół).
 *
 * Lista menu tenanta w dwóch sekcjach: MODUŁY (kolorowe ikony sidebara: Systemowe, BHP, Urlopy…)
 * oraz MENU WŁASNE (custom layouty z Kreatora menu). Dla wybranego podmiotu (użytkownik / rola /
 * zespół): które menu widzi (checkbox) + jedno DOMYŚLNE (gwiazdka), otwierane po zalogowaniu.
 * Domyślnie podmiot widzi WSZYSTKIE menu; odznaczenie ukrywa pozycję. Panel samodzielny — własny
 * zapis (replace-all: moduły + layouty + domyślne w jednym żądaniu).
 *
 * Łączenie źródeł u usera jest kaskadowe (rola → zespół → user); tu edytujemy JEDNĄ warstwę.
 * Główny administrator (super-user) i tak widzi wszystkie menu.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Star } from 'lucide-react';

import { useToast } from '@/contexts/ToastContext';
import { useMenuLayouts } from '@/hooks/useMenuLayouts';
import {
  type MenuDefaultRef,
  type MenuSubject,
  useAvailableMenuModules,
  useMenuAssignments,
  useUpdateMenuAssignments,
} from '@/lib/use-permission-center';

import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { IconButton } from '../IconButton';
import { Spinner } from '../Spinner';

interface MenuAssignmentsPanelProps {
  subject: MenuSubject;
  /** Firma, której dotyczą ustawienia menu — menu jest per firma (migracja 0175). */
  companyId: string | null;
  canManage: boolean;
}

/** Klucz porównywalny domyślnego (kind:ref) do prostego równania. */
function defKey(def: MenuDefaultRef | null): string | null {
  return def ? `${def.kind}:${def.ref}` : null;
}

export function MenuAssignmentsPanel({ subject, companyId, canManage }: MenuAssignmentsPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const modulesQuery = useAvailableMenuModules();
  const layoutsQuery = useMenuLayouts();
  const assignmentsQuery = useMenuAssignments(subject, companyId);
  const update = useUpdateMenuAssignments(subject, companyId);

  const modules = useMemo(() => modulesQuery.data?.modules ?? [], [modulesQuery.data]);
  const layouts = useMemo(
    () => layoutsQuery.data?.layouts.filter((l) => !l.isEmpty) ?? [],
    [layoutsQuery.data],
  );

  // Wariant tekstów per rodzaj podmiotu (użytkownik / rola / zespół).
  const subjectWord = t(`tenant.permissions.center.menu.subjectWord.${subject.kind}`);

  // Brudnopis lokalny. Brak nadpisania = wszystko zaznaczone (pełny dostęp domyślnie).
  const [availModules, setAvailModules] = useState<Set<string>>(new Set());
  const [availLayouts, setAvailLayouts] = useState<Set<string>>(new Set());
  const [def, setDef] = useState<MenuDefaultRef | null>(null);

  const server = assignmentsQuery.data;
  const allModuleCodes = useMemo(() => modules.map((m) => m.code), [modules]);
  const allLayoutIds = useMemo(() => layouts.map((l) => l.id), [layouts]);

  useEffect(() => {
    if (!server || (modules.length === 0 && layouts.length === 0)) return;
    if (server.hasOverride) {
      setAvailModules(new Set(server.modules));
      setAvailLayouts(new Set(server.layouts));
      setDef(server.default);
    } else {
      // Pełny dostęp domyślnie — wszystko zaznaczone, brak wskazanego domyślnego.
      setAvailModules(new Set(allModuleCodes));
      setAvailLayouts(new Set(allLayoutIds));
      setDef(null);
    }
  }, [server, modules, layouts, allModuleCodes, allLayoutIds, subject.id, subject.kind, companyId]);

  const baseline = useMemo(() => {
    if (!server) return null;
    return server.hasOverride
      ? { mods: new Set(server.modules), lays: new Set(server.layouts), def: server.default }
      : { mods: new Set(allModuleCodes), lays: new Set(allLayoutIds), def: null };
  }, [server, allModuleCodes, allLayoutIds]);

  const dirty = useMemo(() => {
    if (!baseline) return false;
    const sameSet = (a: Set<string>, b: Set<string>) =>
      a.size === b.size && [...a].every((x) => b.has(x));
    return (
      !sameSet(baseline.mods, availModules) ||
      !sameSet(baseline.lays, availLayouts) ||
      defKey(baseline.def) !== defKey(def)
    );
  }, [baseline, availModules, availLayouts, def]);

  const toggleModule = (code: string, next: boolean) => {
    setAvailModules((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(code);
      else copy.delete(code);
      return copy;
    });
    if (!next && def?.kind === 'module' && def.ref === code) setDef(null);
  };

  const toggleLayout = (id: string, next: boolean) => {
    setAvailLayouts((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
    if (!next && def?.kind === 'layout' && def.ref === id) setDef(null);
  };

  const toggleDefault = (ref: MenuDefaultRef) => {
    // Gwiazdka toggluje: klik na już-domyślnym zdejmuje; inaczej ustawia (i włącza dostępność).
    if (def && def.kind === ref.kind && def.ref === ref.ref) {
      setDef(null);
      return;
    }
    if (ref.kind === 'module') setAvailModules((prev) => new Set(prev).add(ref.ref));
    else setAvailLayouts((prev) => new Set(prev).add(ref.ref));
    setDef(ref);
  };

  const reset = () => {
    if (!baseline) return;
    setAvailModules(new Set(baseline.mods));
    setAvailLayouts(new Set(baseline.lays));
    setDef(baseline.def);
  };

  const save = async () => {
    try {
      await update.mutateAsync({
        modules: Array.from(availModules),
        layouts: Array.from(availLayouts),
        default: def,
      });
      toast.show({ variant: 'success', title: t('tenant.permissions.center.menu.saved') });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.permissions.center.menu.saveFailed') });
    }
  };

  if (modulesQuery.isLoading || layoutsQuery.isLoading || assignmentsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (modules.length === 0 && layouts.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm text-text-secondary">
        {t('tenant.permissions.center.menu.empty')}
      </div>
    );
  }

  const renderStar = (
    isDefault: boolean,
    isAvailable: boolean,
    ref: MenuDefaultRef,
    name: string,
  ) => (
    <IconButton
      size="sm"
      aria-label={
        isDefault
          ? t('tenant.permissions.center.menu.isDefaultAria', { name })
          : t('tenant.permissions.center.menu.setDefaultAria', { name })
      }
      title={
        isDefault
          ? t('tenant.permissions.center.menu.isDefault')
          : t('tenant.permissions.center.menu.setDefault')
      }
      disabled={!canManage || (!isAvailable && !isDefault)}
      onClick={() => toggleDefault(ref)}
      className={isDefault ? 'text-amber-500' : 'text-text-muted'}
    >
      <Star className="h-4 w-4" fill={isDefault ? 'currentColor' : 'none'} aria-hidden="true" />
    </IconButton>
  );

  return (
    <div className="overflow-x-auto">
      <p className="px-3 pt-2 text-sm text-text-secondary">
        {t('tenant.permissions.center.menu.intro', { subject: subjectWord })}
      </p>

      {/* Rozróżnienie „brak nadpisania (dziedziczy)” od „nadpisanie: wszystko” — inaczej wyglądają
          tak samo (wszystko zaznaczone), a działają różnie. */}
      {server && !server.hasOverride && (
        <p className="mx-3 mt-2 flex items-start gap-2 rounded-md bg-bg-secondary px-3 py-2 text-xs text-text-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('tenant.permissions.center.menu.inheritsHint')}
        </p>
      )}

      <div className="mt-2 border-b border-border-primary" />

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-primary text-left text-text-secondary">
            <th className="px-3 py-2 font-medium">{t('tenant.permissions.center.menu.colMenu')}</th>
            <th className="w-28 px-3 py-2 text-center font-medium">
              {t('tenant.permissions.center.menu.colAvailable')}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Sekcja: Moduły */}
          <tr className="bg-bg-secondary/40">
            <td colSpan={2} className="px-3 py-1.5 text-xs font-semibold uppercase text-text-muted">
              {t('tenant.permissions.center.menu.sectionModules')}
            </td>
          </tr>
          {modules.map((mod) => {
            const isAvailable = availModules.has(mod.code);
            const ref: MenuDefaultRef = { kind: 'module', ref: mod.code };
            const isDefault = def?.kind === 'module' && def.ref === mod.code;
            return (
              <tr key={`m:${mod.code}`} className="border-b border-border-primary/50">
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1.5">
                    {renderStar(isDefault, isAvailable, ref, mod.name)}
                    <span className="text-text-primary">{mod.name}</span>
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="flex justify-center">
                    <Checkbox
                      checked={isAvailable}
                      disabled={!canManage}
                      onChange={(e) => toggleModule(mod.code, e.target.checked)}
                      aria-label={t('tenant.permissions.center.menu.availableAria', {
                        name: mod.name,
                      })}
                    />
                  </span>
                </td>
              </tr>
            );
          })}

          {/* Sekcja: Menu własne (custom layouty) — tylko gdy są jakieś. */}
          {layouts.length > 0 && (
            <tr className="bg-bg-secondary/40">
              <td
                colSpan={2}
                className="px-3 py-1.5 text-xs font-semibold uppercase text-text-muted"
              >
                {t('tenant.permissions.center.menu.sectionLayouts')}
              </td>
            </tr>
          )}
          {layouts.map((lay) => {
            const isAvailable = availLayouts.has(lay.id);
            const ref: MenuDefaultRef = { kind: 'layout', ref: lay.id };
            const isDefault = def?.kind === 'layout' && def.ref === lay.id;
            return (
              <tr key={`l:${lay.id}`} className="border-b border-border-primary/50">
                <td className="px-3 py-2">
                  <span className="flex items-center gap-1.5">
                    {renderStar(isDefault, isAvailable, ref, lay.name)}
                    <span className="text-text-primary">{lay.name}</span>
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="flex justify-center">
                    <Checkbox
                      checked={isAvailable}
                      disabled={!canManage}
                      onChange={(e) => toggleLayout(lay.id, e.target.checked)}
                      aria-label={t('tenant.permissions.center.menu.availableAria', {
                        name: lay.name,
                      })}
                    />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-2.5 flex flex-col gap-1 px-3 text-xs text-text-muted">
        <span className="flex items-center gap-2">
          <Star className="h-3.5 w-3.5" aria-hidden="true" />
          {t('tenant.permissions.center.menu.defaultStarHint')}
        </span>
        <span className="flex items-center gap-2">
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
          {t('tenant.permissions.center.menu.superAdminNote')}
        </span>
      </div>

      {canManage && (
        <div className="mt-3.5 flex justify-end gap-2 px-3 pb-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={reset}
          >
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            loading={update.isPending}
            disabled={!dirty}
            onClick={() => void save()}
          >
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
